"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";

import { logger } from "@/lib/logger";
import { isChunkLoadError } from "@/lib/pwa/chunk-load-error";
import { canAutoReload, isNewVersionAvailable } from "@/lib/pwa/version";

const SERVICE_WORKER_PATH = "/sw.js";
const SERVICE_WORKER_SCOPE = "/";

const ACTION_LABELS: Record<string, string> = {
  toggleContacted: "Contractor contact update",
  saveNotes: "Contractor notes",
  updatePriceRange: "Contractor price range",
  updateRating: "Contractor rating",
};

function resolveQueueLabel(url?: string, action?: string) {
  if (typeof action === "string" && action in ACTION_LABELS) {
    return ACTION_LABELS[action];
  }
  if (typeof url === "string") {
    if (url.includes("/api/deployments")) {
      return "Team deployment";
    }
    if (url.includes("/api/contractors")) {
      return "Contractor update";
    }
  }
  return "Queued request";
}

function parseQueuedAction(raw?: unknown): { action?: string } | undefined {
  if (typeof raw !== "string") {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as { action?: string } | null;
    if (parsed !== null && typeof parsed === "object") {
      return parsed;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

export function ServiceWorkerProvider() {
  const router = useRouter();
  // Keep the router reference current without re-subscribing the SW message
  // listener on every router change (it's registered once in the effect).
  const routerRef = useRef(router);
  routerRef.current = router;

  // A stale tab can request a chunk that no longer exists after a deployment.
  // A full reload is the only reliable recovery, but doing it automatically
  // could drop an active call or unsaved work. Offer the recovery once and let
  // the user choose the safe moment.
  useEffect(() => {
    let prompted = false;
    const offerReload = () => {
      if (prompted) return;
      prompted = true;
      toast("This page needs the latest version", {
        description: "Reload to recover from an outdated application file.",
        duration: Infinity,
        action: {
          label: "Reload",
          onClick: () => window.location.reload(),
        },
      });
    };
    const handleWindowError = (event: ErrorEvent) => {
      if (isChunkLoadError(event.error, event.message)) offerReload();
    };
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason)) offerReload();
    };

    window.addEventListener("error", handleWindowError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    return () => {
      window.removeEventListener("error", handleWindowError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
    };
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return;
    }
    // Homeowners on the online-booking page (often framed on a contractor's
    // site) are not Atriam users: installing Atriam's worker for them would
    // cache and intercept atriam.ai for nothing.
    if (/^\/book(?:\/|$)/.test(window.location.pathname)) {
      return;
    }

    const postProcessQueueMessage = () => {
      const payload = { type: "PROCESS_QUEUE" as const };
      const controller = navigator.serviceWorker.controller;
      if (controller) {
        controller.postMessage(payload);
        return;
      }
      void navigator.serviceWorker.ready
        .then((registration) => {
          registration.active?.postMessage(payload);
        })
        .catch(() => {});
    };

    const handleOnline = () => {
      postProcessQueueMessage();
    };

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      const data = event.data as
        | {
            type?: string;
            url?: string;
            error?: string;
            body?: string;
            reason?: string;
            attempts?: number;
          }
        | undefined;
      if (!data || typeof data.type !== "string") {
        return;
      }

      const payload = parseQueuedAction(data.body);
      const label = resolveQueueLabel(data.url, payload?.action);

      switch (data.type) {
        case "PUSH_RECEIVED": {
          break;
        }
        case "QUEUE_ADDED": {
          toast("Working offline", {
            description: `${label} queued for sync`,
            duration: 2500,
          });
          break;
        }
        case "QUEUE_PROCESSED": {
          toast.success(`${label} synced successfully`);
          break;
        }
        case "QUEUE_RETRY": {
          toast.error(
            `${label} failed to sync. Will retry automatically. ${data.error ?? ""}`.trim(),
          );
          break;
        }
        case "QUEUE_DROPPED": {
          const reason =
            data.reason === "unauthorized"
              ? "Sign in again to retry."
              : "Reached retry limit.";
          toast.error(`${label} could not sync. ${reason}`.trim());
          break;
        }
        case "QUEUE_CLEARED": {
          toast("Offline queue cleared", {
            description: "Pending requests were removed.",
            duration: 2500,
          });
          break;
        }
        case "sw-navigate": {
          // Client-side route change requested by the SW notificationclick
          // handler. router.push (not a document load) preserves any live call.
          const url = data.url;
          if (typeof url === "string") {
            routerRef.current.push(url);
          }
          break;
        }
        case "OFFLINE_FALLBACK": {
          // The SW just served the offline shell for a failed navigation.
          // Stash the route the user wanted so offline.html can send them back
          // there once the connection returns, instead of dropping them at "/".
          const url = data.url;
          if (typeof url === "string" && url.startsWith("/")) {
            try {
              sessionStorage.setItem("atriam.offline.returnTo", url);
            } catch {
              // Private mode / storage disabled — the shell falls back to "/".
            }
          }
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener("online", handleOnline);
    navigator.serviceWorker.addEventListener(
      "message",
      handleServiceWorkerMessage,
    );

    // A controller change means a new SW took over mid-session. The old code
    // hard-reloaded the tab here — a full document load that drops live Telnyx
    // calls and unsaved work, contradicting this file's own "we never
    // auto-reload" policy below. Prompt instead: the user picks a safe moment,
    // exactly like the version-poll update offer.
    const hadInitialController = !!navigator.serviceWorker.controller;
    let prompted = false;
    const handleControllerChange = () => {
      if (prompted) return;
      // Only relevant if a controller was already in place when the page
      // loaded — the first SW taking control of a fresh tab needs no reload.
      if (!hadInitialController) return;
      prompted = true;
      toast("A new version is available", {
        description: "Reload to get the latest fixes.",
        duration: Infinity,
        action: {
          label: "Reload",
          onClick: () => window.location.reload(),
        },
      });
    };
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      handleControllerChange,
    );

    // Named handlers + effect-scoped refs so the async-added SW lifecycle
    // listeners below can be removed in the cleanup, even though the targets
    // only exist once registration resolves.
    let registeredSw: ServiceWorkerRegistration | null = null;
    let installingSw: ServiceWorker | null = null;
    const handleInstallingStateChange = () => {
      if (!installingSw) return;
      if (
        installingSw.state === "installed" &&
        navigator.serviceWorker.controller
      ) {
        installingSw.postMessage({ type: "SKIP_WAITING" });
      }
    };
    const handleUpdateFound = () => {
      const installing = registeredSw?.installing;
      if (!installing) return;
      installingSw = installing;
      // Removed in the effect cleanup via installingSw; the rule can't trace a
      // listener whose target is obtained inside this async registration flow.
      // eslint-disable-next-line @eslint-react/web-api-no-leaked-event-listener -- removeEventListener("statechange", handleInstallingStateChange) runs in the effect cleanup via installingSw
      installing.addEventListener("statechange", handleInstallingStateChange);
    };

    // Register service worker. Failure here is consequential — push
    // notifications, offline behavior, and the incoming-call ringer all
    // depend on the SW being live. A swallowed error here was the cause
    // of "phones don't ring on inbound calls" silently in production for
    // users on insecure contexts (HTTP localhost) or users with corp
    // proxies that block /sw.js fetches. Surface it.
    void navigator.serviceWorker
      .register(SERVICE_WORKER_PATH, { scope: SERVICE_WORKER_SCOPE })
      .then((registration: ServiceWorkerRegistration | undefined) => {
        // Some embedded browsers and dev shells stub register() and resolve
        // with nothing; there is no worker to update or listen to then.
        if (!registration) {
          logger.warn("[SW] register() resolved without a registration", {
            path: SERVICE_WORKER_PATH,
          });
          return;
        }
        registeredSw = registration;
        postProcessQueueMessage();
        // Force an immediate update check. Browsers normally revalidate
        // the SW script every 24h, but Safari/iOS can be stickier and
        // some users are still controlled by an old fetch-handling SW
        // long past the deploy that retired it.
        void registration.update().catch(() => {});
        // If a new SW is already waiting (skipWaiting was called during
        // install but the old one is still active), nudge it to take
        // over immediately.
        if (registration.waiting) {
          registration.waiting.postMessage({ type: "SKIP_WAITING" });
        }
        // Removed in the effect cleanup via registeredSw; the rule can't trace a
        // listener whose target is obtained inside this async registration flow.
        // eslint-disable-next-line @eslint-react/web-api-no-leaked-event-listener -- removeEventListener("updatefound", handleUpdateFound) runs in the effect cleanup via registeredSw
        registration.addEventListener("updatefound", handleUpdateFound);
      })
      .catch((err: unknown) => {
        logger.error(
          "[SW] Service worker registration failed — push notifications and offline will not work",
          {
            error: err,
            path: SERVICE_WORKER_PATH,
            scope: SERVICE_WORKER_SCOPE,
          },
        );
      });

    return () => {
      window.removeEventListener("online", handleOnline);
      navigator.serviceWorker.removeEventListener(
        "message",
        handleServiceWorkerMessage,
      );
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        handleControllerChange,
      );
      registeredSw?.removeEventListener("updatefound", handleUpdateFound);
      installingSw?.removeEventListener(
        "statechange",
        handleInstallingStateChange,
      );
    };
  }, []);

  // Detect a newer deploy and offer a reload, so an open PWA isn't stuck on a
  // stale bundle. The SW only handles two allowlisted field mutations and does
  // not cache the app, so /sw.js can be byte-identical across deploys and its
  // update path never fires for a plain code deploy. Poll a tiny version
  // endpoint instead. A visible tab is prompted; a stale tab reloads itself
  // only while hidden with no call up and no typing (canAutoReload), since a
  // tab left open across deploys stops working (its browser phone never
  // rang on 2026-10-18).
  useEffect(() => {
    let loaded: string | null = null;
    let prompted = false;
    let stale = false;
    const reloadIfSafe = () => {
      if (stale && canAutoReload(document)) window.location.reload();
    };

    const check = () => {
      void fetch("/api/version", { cache: "no-store" })
        .then((res) =>
          res.ok ? (res.json() as Promise<{ version?: string }>) : null,
        )
        .then((data) => {
          const current = data?.version;
          if (loaded === null) {
            if (typeof current === "string" && current.length > 0) {
              loaded = current;
            }
            return;
          }
          if (isNewVersionAvailable(loaded, current)) {
            stale = true;
            reloadIfSafe();
          }
          if (!prompted && stale) {
            prompted = true;
            toast("A new version is available", {
              description: "Reload to get the latest fixes.",
              duration: Infinity,
              action: {
                label: "Reload",
                onClick: () => window.location.reload(),
              },
            });
          }
        })
        .catch(() => {});
    };

    check();
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
      else reloadIfSafe();
    };
    document.addEventListener("visibilitychange", onVisible);
    const timer = setInterval(check, 15 * 60 * 1000);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(timer);
    };
  }, []);

  return null;
}
